import React, { useState } from 'react';
import { settingsAPI } from '../../services/api';
import { useAuth } from '../../hooks/useAuth';
import { useForm } from 'react-hook-form';
import { 
  UserIcon, 
  KeyIcon, 
  CogIcon,
  BellIcon,
  ShieldCheckIcon,
  BanknotesIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

// Inline Metal Rates pane using RatesContext
import { useRates } from '../../contexts/RatesContext';
const Settings = () => {
  const { user, updateProfile, changePassword } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');

  const tabs = [
    { id: 'profile', name: 'Profile', icon: UserIcon },
    { id: 'password', name: 'Password', icon: KeyIcon },
    { id: 'preferences', name: 'Preferences', icon: CogIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'security', name: 'Security', icon: ShieldCheckIcon },
    { id: 'metal_rates', name: 'Metal Rates', icon: BanknotesIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-primary-700">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage your account settings and preferences
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full group flex items-center px-3 py-2 text-sm font-medium rounded-md ${
                    activeTab === tab.id
                      ? 'bg-primary-100 text-primary-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-primary-50'
                  }`}
                >
                  <Icon
                    className={`mr-3 h-5 w-5 flex-shrink-0 ${
                      activeTab === tab.id
                        ? 'text-primary-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                    }`}
                  />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="bg-white shadow rounded-lg">
            {activeTab === 'profile' && <ProfileSettings user={user} onUpdate={updateProfile} />}
            {activeTab === 'password' && <PasswordSettings onChangePassword={changePassword} />}
            {activeTab === 'preferences' && <PreferencesSettings />}
            {activeTab === 'notifications' && <NotificationsSettings />}
            {activeTab === 'security' && <SecuritySettings />}
            {activeTab === 'metal_rates' && <MetalRatesSettings />}
          </div>
        </div>
      </div>
    </div>
  );
};

// Profile Settings Component
const ProfileSettings = ({ user, onUpdate }) => {
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      full_name: user?.full_name || '',
      email: user?.email || '',
    }
  });

  const onSubmit = async (data) => {
    const result = await onUpdate(data);
    if (result.success) {
      toast.success('Profile updated successfully');
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Profile Information</h3>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
            Full Name
          </label>
          <input
            type="text"
            {...register('full_name', { required: 'Full name is required' })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          {errors.full_name && (
            <p className="mt-1 text-sm text-red-600">{errors.full_name.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email Address
          </label>
          <input
            type="email"
            {...register('email', { 
              required: 'Email is required',
              pattern: {
                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                message: 'Invalid email address'
              }
            })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

// Password Settings Component
const PasswordSettings = ({ onChangePassword }) => {
  const { register, handleSubmit, formState: { errors }, watch } = useForm();
  const newPassword = watch('new_password');

  const onSubmit = async (data) => {
    const result = await onChangePassword({
      current_password: data.current_password,
      new_password: data.new_password,
    });
    if (result.success) {
      // Reset form
      document.getElementById('password-form').reset();
    }
  };

  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
      <form id="password-form" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label htmlFor="current_password" className="block text-sm font-medium text-gray-700">
            Current Password
          </label>
          <input
            type="password"
            {...register('current_password', { required: 'Current password is required' })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          {errors.current_password && (
            <p className="mt-1 text-sm text-red-600">{errors.current_password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="new_password" className="block text-sm font-medium text-gray-700">
            New Password
          </label>
          <input
            type="password"
            {...register('new_password', { 
              required: 'New password is required',
              minLength: {
                value: 6,
                message: 'Password must be at least 6 characters'
              }
            })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          {errors.new_password && (
            <p className="mt-1 text-sm text-red-600">{errors.new_password.message}</p>
          )}
        </div>

        <div>
          <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700">
            Confirm New Password
          </label>
          <input
            type="password"
            {...register('confirm_password', { 
              required: 'Please confirm your password',
              validate: value => value === newPassword || 'Passwords do not match'
            })}
            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
          />
          {errors.confirm_password && (
            <p className="mt-1 text-sm text-red-600">{errors.confirm_password.message}</p>
          )}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Change Password
          </button>
        </div>
      </form>
    </div>
  );
};

// Preferences Settings Component
const PreferencesSettings = () => {
  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Preferences</h3>
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Language</h4>
          <select className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm">
            <option value="en">English</option>
            <option value="hi">Hindi</option>
          </select>
        </div>
        
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Date Format</h4>
          <select className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm">
            <option value="dd/mm/yyyy">DD/MM/YYYY</option>
            <option value="mm/dd/yyyy">MM/DD/YYYY</option>
            <option value="yyyy-mm-dd">YYYY-MM-DD</option>
          </select>
        </div>

        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Currency</h4>
          <select className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm">
            <option value="INR">Indian Rupee (₹)</option>
            <option value="USD">US Dollar ($)</option>
            <option value="EUR">Euro (€)</option>
          </select>
        </div>
      </div>
    </div>
  );
};

// Notifications Settings Component
const NotificationsSettings = () => {
  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Notifications</h3>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Email Notifications</h4>
            <p className="text-sm text-gray-500">Receive notifications via email</p>
          </div>
          <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">SMS Notifications</h4>
            <p className="text-sm text-gray-500">Receive notifications via SMS</p>
          </div>
          <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-medium text-gray-900">Payment Reminders</h4>
            <p className="text-sm text-gray-500">Get reminded about upcoming payments</p>
          </div>
          <input type="checkbox" className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded" />
        </div>
      </div>
    </div>
  );
};

// Security Settings Component
const SecuritySettings = () => {
  return (
    <div className="p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Security</h3>
      <div className="space-y-6">
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Two-Factor Authentication</h4>
          <p className="text-sm text-gray-500 mb-3">
            Add an extra layer of security to your account
          </p>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
            Enable 2FA
          </button>
        </div>
        
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-2">Login Activity</h4>
          <p className="text-sm text-gray-500 mb-3">
            View your recent login activity
          </p>
          <button className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
            View Activity
          </button>
        </div>
      </div>
    </div>
  );
};

export default Settings;



function MetalRatesSettings() {
  const { rates, updateRate, canEdit, replaceRates } = useRates();
  const [syncing, setSyncing] = useState(false);
  const handleChange = (metal, purity) => (e) => updateRate(metal, purity, e.target.value);
  const syncFromGoldAPI = async () => {
    if (!canEdit || syncing) return;
    setSyncing(true);
    try {
      const res = await settingsAPI.syncMetalRates();
      const newRates = res?.data?.data;
      if (newRates) {
        replaceRates(newRates, false);
      }
    } catch (_) {
      // silent
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-medium text-gray-900">Metal Rates</h3>
        <button
          onClick={syncFromGoldAPI}
          disabled={!canEdit || syncing}
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          <ArrowPathIcon className={`h-5 w-5 ${syncing ? 'animate-spin' : ''}`} />
          <span>{syncing ? 'Syncing…' : 'Sync from GoldAPI'}</span>
        </button>
      </div>
      <p className="text-sm text-gray-500 mb-6">Rates are fetched from server and can be adjusted if you have permission.</p>
      <div className="space-y-6">
        <section>
          <h4 className="text-md font-semibold mb-3">Gold (per gram)</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Object.keys(rates.gold).map((purity) => (
              <div key={purity}>
                <label className="block text-sm font-medium text-gray-700">{purity}</label>
                <input
                  type="number"
                  step="0.01"
                  className="input mt-1"
                  value={rates.gold[purity] || 0}
                  onChange={handleChange('gold', purity)}
                  disabled={!canEdit}
                />
              </div>
            ))}
          </div>
        </section>
        <section>
          <h4 className="text-md font-semibold mb-3">Silver (per gram)</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {Object.keys(rates.silver).map((purity) => (
              <div key={purity}>
                <label className="block text-sm font-medium text-gray-700">{purity}</label>
                <input
                  type="number"
                  step="0.01"
                  className="input mt-1"
                  value={rates.silver[purity] || 0}
                  onChange={handleChange('silver', purity)}
                  disabled={!canEdit}
                />
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
